import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const About = () => {
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about Lloyd Funding — a trusted partner helping small businesses access fast, flexible funding solutions."
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              About <span className="text-blue-700">Lloyd Funding</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              We're on a mission to make business funding accessible, fast, and
              transparent for small businesses across America.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#F8FAFC"
            />
          </svg>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
                Our Story
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Lloyd Funding was founded with a simple belief: small businesses
                are the backbone of our economy, and they deserve better access
                to capital.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Traditional banks often overlook small businesses, requiring
                mountains of paperwork, weeks of waiting, and perfect credit
                scores. We knew there had to be a better way.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Today, we've helped thousands of businesses secure the funding
                they need to grow, hire, and thrive. Our streamlined process
                means you can apply in minutes and get funded as soon as the
                same day.
              </p>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-blue-200/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <div className="aspect-[4/3] w-full">
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1000"
                    alt="Team collaboration"
                    className="h-full w-full object-cover"
                    width={1000}
                    height={750}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Our Values
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Transparency",
                description:
                  "No hidden fees, no surprises. We believe in clear, upfront terms so you always know exactly what you're getting.",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                ),
              },
              {
                title: "Speed",
                description:
                  "Time is money. Our streamlined process gets you from application to funding in as little as 3 hours.",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                ),
              },
              {
                title: "Partnership",
                description:
                  "We're not just a lender — we're your partner in growth. Your success is our success.",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                  </svg>
                ),
              },
            ].map((value) => (
              <div
                key={value.title}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <div className="inline-flex rounded-2xl bg-blue-100 p-3 text-blue-600">
                  {value.icon}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-slate-800">
                  {value.title}
                </h3>
                <p className="mt-2 text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-blue-800">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {[
              { value: "$30M+", label: "Funded to businesses" },
              { value: "1,000+", label: "Businesses helped" },
              { value: "24 hrs", label: "Average funding time" },
              { value: "10+ Years", label: "Industry experience" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl font-bold text-white sm:text-4xl">
                  {stat.value}
                </p>
                <p className="mt-2 text-blue-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Ready to grow your business?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Join thousands of businesses that trust Lloyd Funding.
            </p>
            <div className="mt-8">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-8 py-4 sm:py-3 text-base sm:text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
